import styled from "@emotion/styled";
import { useConnection } from "@solana/wallet-adapter-react";
import { PublicKey, Transaction, TransactionInstruction } from "@solana/web3.js";
import { Button, Switch } from "antd";
import { encode as encode_bs58 } from "bs58";
import jwtDecode from "jwt-decode";
import { type FC, useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useNetworkConfig } from "../../contexts/app/config";
import { useEnrichedWallet } from "../../contexts/enrichedWallet";
import { useRefreshContext } from "../../contexts/refresh";
import { useStepAuthStore } from "../../hooks/stores/stepAuth";
import { STEP_API_ENDPOINT } from "../../utils/swr";
import { Modal } from "../Modal/Modal";
import { TextDescription } from "../shared";

const MEMO_PROGRAM_ID = new PublicKey("MemoSq4gqABAXKb96qnH8TysNcWxMyWCqXgDLGmfcHr");

export const StepAuth: FC<any> = ({ children }) => {
  const [ledger, setLedger] = useState(false);
  const { connection } = useConnection();
  const { network } = useNetworkConfig();
  const wallet = useEnrichedWallet();
  const [jwt, setJwt, deleteJwt] = useStepAuthStore((state) => [state.jwt, state.setJwt, state.deleteJwt]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { refreshID } = useRefreshContext();
  const navigate = useNavigate();

  useEffect(() => {
    const wrapper = async () => {
      const jwtDecoded: { publicKey: string; exp: number } = jwt ? jwtDecode(jwt) : { publicKey: "", exp: 0 };
      const jwtExpired = jwtDecoded.exp < Date.now() / 1000 + 600;
      const jwtMatched = Boolean(wallet.publicKey && jwtDecoded.publicKey === wallet.publicKey.toBase58());
      if (jwtExpired || !jwtMatched) {
        setIsModalOpen(true);
        jwt && deleteJwt();
      } else if (!jwtExpired && jwtMatched && isModalOpen) {
        setIsModalOpen(false);
      }
    };
    void wrapper();
  }, [isModalOpen, jwt, wallet, refreshID, deleteJwt]);

  const authorize = useCallback(() => {
    const wrapper = async () => {
      let auth;
      if (wallet && wallet.signMessage && !ledger) {
        const timestamp = Math.floor(Date.now() / 1000);
        const message = `step-api-auth:${timestamp}`;
        const encodedMessage = new TextEncoder().encode(message);
        const signature = await wallet.signMessage(new Uint8Array(encodedMessage));
        const encodedSig = encode_bs58(signature);
        auth = {
          signature: encodedSig,
          publicKey: wallet.publicKey?.toBase58(),
          timestamp: timestamp,
          cluster: network,
        };
      } else if (wallet && wallet.signTransaction && ledger) {
        const timestamp = Math.floor(Date.now() / 1000);
        const tx = new Transaction();
        tx.instructions = [
          new TransactionInstruction({
            programId: MEMO_PROGRAM_ID,
            keys: [],
            data: Buffer.from(`step-api-auth:${timestamp}`, "utf-8"),
          }),
        ];
        tx.feePayer = wallet.publicKey ?? undefined;
        tx.recentBlockhash = (await connection.getLatestBlockhash()).blockhash;
        const signedTx = await wallet.signTransaction(tx);
        const txBase64 = signedTx.serialize().toString("base64");
        auth = {
          transaction: txBase64,
          publicKey: wallet.publicKey?.toBase58(),
          timestamp: timestamp,
          cluster: network,
        };
      }
      const response = await fetch(`${STEP_API_ENDPOINT}/auth/login`, {
        method: "POST",
        body: JSON.stringify({
          auth,
        }),
        headers: {
          "Content-Type": "application/json",
        },
      });
      const json = await response.json();
      if (json.access_token) {
        setJwt(json.access_token);
        setIsModalOpen(false);
      }
    };
    void wrapper();
  }, [connection, ledger, network, setJwt, wallet]);
  return (
    <>
      <Modal title="Analyze your history" visible={isModalOpen} onClose={() => navigate(-1)} width={420}>
        <ModalBody>
          <TextDescription className="description">
            In order to view your transactions in detail, we need to make sure it&apos;s you. Please sign with your
            wallet.
          </TextDescription>
          <StyledButton onClick={authorize}>Sign</StyledButton>
          <LedgerSpan>
            <span>Use my Ledger to sign</span>
            <Switch onChange={setLedger}></Switch>
          </LedgerSpan>
        </ModalBody>
      </Modal>
      {jwt && children}
    </>
  );
};

const ModalBody = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const StyledButton = styled(Button)`
  width: 35%;
  margin-top: 32px;
`;

const LedgerSpan = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  margin-top: 12px;
  span {
    margin-right: 8px;
  }
`;
